import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import { EmailType } from '@/types/brandedTypes/email';

// Lib
import { emailValid } from '@/lib/regex';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
// Components
import Modal from '@/components/base/Modal';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';

type ContentProps = Omit<ExportXlsModalProps, 'isOpen'>;
const Content = ({
    isLocked,
    toggle,
    title,
    sendReport,
    confirmButtonClassName,
    confirmButtonId,
    headerExtraContent,
    footerExtraButton,
    children
}: ContentProps) => {
    const { t } = useTranslation('commons');
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [formState, setFormState] = useState<{
        success?: string;
        error?: string;
        email_error?: string;
        isSubmitting: boolean;
        isDefaultAddress: boolean;
    }>({
        success: null,
        error: null,
        email_error: null,
        isSubmitting: false,
        isDefaultAddress: true
    });

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormState((prevState) => ({
            ...prevState,
            error: null,
            email_error: null,
            success: null,
            isSubmitting: true
        }));

        const form = e.currentTarget;
        const formElements = form.elements as typeof form.elements & {
            email: HTMLInputElement;
        };
        const userEmail = user?.email as EmailType;
        const inputValue = (formElements?.email?.value as EmailType) ?? null;
        const emailToSend = formState?.isDefaultAddress ? userEmail : inputValue;

        if (!formState?.isDefaultAddress && !emailValid.test(emailToSend)) {
            setFormState((prevState) => ({
                ...prevState,
                isSubmitting: false,
                email_error: t(
                    emailToSend === '' ? 'errors:error_email_required' : 'errors:error_email_email'
                )
            }));
        } else {
            try {
                await sendReport(emailToSend);
                setFormState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    success: t('export_xls.export_success')
                }));
            } catch (e) {
                setFormState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('errors:error_occured_submit_data')
                }));
            }
        }
    };

    return (
        <form onSubmit={onSubmit}>
            <Modal.Body>
                {formState.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={formState.error}
                        onClose={() => {
                            setFormState((prevState) => ({
                                ...prevState,
                                isSubmitting: false,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                {formState.success ? (
                    <InfoBanner
                        type="success"
                        className="mb-4"
                        title={t('commons:success')}
                        message={formState.success}
                        onClose={() => {
                            setFormState((prevState) => ({
                                ...prevState,
                                isSubmitting: false,
                                success: null
                            }));
                        }}
                    />
                ) : null}
                <Modal.Title>{title ?? t('export_xls.title')}</Modal.Title>
                <Modal.Content className="mt-5">
                    {headerExtraContent}
                    <div className="mt mb-8 space-y-5 rounded-md bg-icoGray-100 p-8 text-center dark:bg-icoDarkMode-deepBlack">
                        <p className="font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {t('export_xls.export_sent_to')}
                        </p>
                        {formState?.isDefaultAddress ? (
                            <p className="truncate rounded-md bg-icoBlue-100 px-3 py-5 text-center font-GSemiBold text-sm text-icoBlue dark:bg-icoBlue/20">
                                {user?.email}
                            </p>
                        ) : (
                            <div className="text-left">
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    aria-label={t('commons:export_xls.label_email_change')}
                                    disabled={formState?.isSubmitting}
                                    placeholder="Email"
                                    error={formState?.email_error}
                                    onChange={(e) => {
                                        if (
                                            formState.email_error &&
                                            (e.target.value === '' ||
                                                emailValid.test(e.target.value))
                                        ) {
                                            setFormState((prevState) => ({
                                                ...prevState,
                                                isSubmitting: false,
                                                email_error: null
                                            }));
                                        }
                                    }}
                                />
                            </div>
                        )}
                        <button
                            type="button"
                            disabled={formState?.isSubmitting || isLocked}
                            className={classNames(
                                'font-GMedium text-xs text-icoBlue underline',
                                isLocked && 'cursor-not-allowed text-icoGray-500 no-underline'
                            )}
                            onClick={() =>
                                setFormState((prevState) => ({
                                    ...prevState,
                                    email_error: null,
                                    success: null,
                                    isDefaultAddress: !prevState?.isDefaultAddress
                                }))
                            }>
                            <span className={isLocked ? 'text-icoGray-500' : ''}>
                                {formState?.isDefaultAddress
                                    ? t('export_xls.button_change_email_to_custom')
                                    : t('export_xls.button_change_email_to_default')}
                            </span>
                            {isLocked && (
                                <TooltipPlanRestriction position="top">
                                    <Icon icon="crown" className="ml-2 text-icoBlue no-underline" />
                                </TooltipPlanRestriction>
                            )}
                        </button>
                    </div>
                    {children}
                </Modal.Content>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className={classNames(
                        'p-0 font-GMedium text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf',
                        { 'hidden sm:inline': footerExtraButton }
                    )}
                    disabled={formState.isSubmitting}
                    onClick={toggle}>
                    {t('commons:close')}
                </button>
                <div className="flex flex-1 justify-end space-x-5">
                    {footerExtraButton}
                    <Button
                        type="submit"
                        id={confirmButtonId}
                        isLoading={formState.isSubmitting}
                        size="lg"
                        className={confirmButtonClassName}>
                        {t('commons:confirm')}
                    </Button>
                </div>
            </Modal.Footer>
        </form>
    );
};

type ExportXlsModalProps = {
    isLocked?: boolean;
    isOpen: boolean;
    toggle: () => void;
    title?: string | React.ReactElement;
    sendReport: (email: EmailType) => Promise<void>;
    confirmButtonClassName?: string;
    confirmButtonId?: string;
    headerExtraContent?: ReactNode;
    footerExtraButton?: ReactNode;
    children?: ReactNode;
};
const ExportXlsModal = ({
    isLocked,
    isOpen,
    toggle,
    title,
    sendReport,
    confirmButtonClassName,
    confirmButtonId,
    headerExtraContent,
    footerExtraButton,
    children
}: ExportXlsModalProps) => {
    return (
        <Modal open={isOpen} onDismiss={toggle} className="!z-100">
            <Content
                isLocked={isLocked ?? false}
                toggle={toggle}
                title={title}
                sendReport={sendReport}
                confirmButtonClassName={confirmButtonClassName}
                confirmButtonId={confirmButtonId}
                headerExtraContent={headerExtraContent}
                footerExtraButton={footerExtraButton}>
                {children}
            </Content>
        </Modal>
    );
};

export default ExportXlsModal;
